import { useCallback, useRef, useState } from 'react';
import {
  IconStatusCorrect,
  Stack,
  Text,
  useToast,
} from '@adc/parallax-component-library';

import type { PatientData } from '~/api/types';
import { PatientInformationForm } from '~/components/forms/PatientInformationForm';
import { Modal } from '~/components/Modal';
import { useModal } from '~/features/modals/useModal';
import type {
  PatientInformationFormValues,
  PatientSettingsFormRef,
  PatientSettingsFormValues,
} from '~/features/patientCreation/types';
import type { FileRouteTypes } from '~/routeTree.gen';
import { buildTestId } from '~/utils/buildTestId';

type PatientEditProfileFormModalProps = {
  testID: string;
  patient: PatientData;
  onCloseRedirectPath: FileRouteTypes['to'];
  onActionComplete?: () => void;
};

export const PatientEditProfileFormModal = ({
  patient,
  testID,
  onActionComplete,
}: PatientEditProfileFormModalProps) => {
  const { closeModal } = useModal();
  const { showToast } = useToast();
  const [hasFormChanged, setHasFormChanged] = useState(false);
  const [formValues, setFormValues] = useState<PatientSettingsFormValues>();
  const [mode, setMode] = useState<'update' | 'confirm'>('update');

  const settingsFormRef = useRef<PatientSettingsFormRef>(null);

  const onFormSubmit = useCallback(
    async (values: PatientInformationFormValues) => {
      // TODO Add update mutation for patient update
      await Promise.resolve();

      closeModal('PatientEditProfileForm');
      onActionComplete?.();

      showToast({
        message: 'Patient profile updated',
        'aria-label': 'Patient profile updated',
        icon: <IconStatusCorrect accentColor="$support.positive.default" />,
      });

      return values;
    },
    [showToast, closeModal, onActionComplete],
  );

  return (
    <Modal
      testID={testID}
      title={
        mode === 'update'
          ? `Edit patient: ${patient.firstName} ${patient.lastName}`
          : 'Changes require patient confirmation'
      }
      hasCloseButton
      size="m"
      primaryAction={{
        label: mode === 'update' ? 'Next' : 'Send request',
        disabled: !hasFormChanged,
        onPress: () => {
          if (mode === 'update') {
            setFormValues(settingsFormRef.current?.getValues());
            setMode('confirm');
          } else {
            void settingsFormRef.current?.submitForm();
          }
        },
      }}
      secondaryAction={{
        label: mode === 'update' ? 'Cancel' : 'Back',
        onPress: () => {
          if (mode === 'update') {
            closeModal('PatientEditProfileForm');
          } else {
            setMode('update');
          }
        },
      }}
      onOpenChange={() => {
        closeModal('PatientEditProfileForm');
      }}
    >
      <Stack
        style={{
          display: mode === 'update' ? 'flex' : 'none',
        }}
      >
        <PatientInformationForm
          ref={settingsFormRef}
          testID={buildTestId({
            testID,
            suffix: ['settings', 'form'],
          })}
          defaultValues={patient}
          onSubmit={(values) => {
            void onFormSubmit(values);
          }}
          hasFormChanged={setHasFormChanged}
          setIsNextDisabled={setHasFormChanged}
        />
      </Stack>
      {mode === 'confirm' && formValues && (
        <Stack flex={1} gap="$6" paddingVertical="$8">
          <Text marginBottom="$6">
            Patient profile changes require the patient’s confirmation. They’ll
            be notified by email and in their CLM mobile app. Once accepted,
            changes will appear in LactateView. MRN changes (if any) apply
            immediately.
          </Text>
        </Stack>
      )}
    </Modal>
  );
};
